import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { LocationHistoryItem } from '@/services/tracking'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Get initials from a name (e.g., "John Doe" -> "JD")
 */
export function getInitials(name: string | undefined | null): string {
  if (!name || typeof name !== 'string') {
    return 'U'; // Default to 'U' for User
  }
  
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Format date to YYYY-MM-DD string using local time (avoids timezone issues)
 * @param date - Date object to format
 * @returns Formatted date string (e.g., "2025-11-08")
 */
export function formatDateToAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date with time set to 00:00:00
 * @returns Date object representing today at midnight
 */
export function getToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Format date for display (e.g., "Nov 12, 2025")
 * @param date - Date object to format
 * @returns Formatted date string
 */
export function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Parse a YYYY-MM-DD string as local date (midnight in local timezone).
 * Use this when comparing with getToday() so "today" works in all timezones.
 * No other util does this: formatDateToAPI is the inverse (Date→string);
 * parseDateTimeLocal is for datetime-local strings that include time.
 */
export function parseLocalDateString(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Check if a date is today
 * @param date - Date to check
 * @returns True if the date is today
 */
export function isToday(date: Date): boolean {
  const today = getToday();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
}

/**
 * Get the previous day from a given date
 * @param date - Date to get previous day from
 * @returns New Date object representing the previous day
 */
export function getPreviousDay(date: Date): Date {
  const prevDate = new Date(date);
  prevDate.setDate(prevDate.getDate() - 1);
  return prevDate;
}

/**
 * Get the next day from a given date
 * @param date - Date to get next day from
 * @returns New Date object representing the next day
 */
export function getNextDay(date: Date): Date {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + 1);
  return nextDate;
}

/**
 * Check if a date is before or equal to another date (ignoring time)
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns True if date1 is before or equal to date2
 */
export function isDateBeforeOrEqual(date1: Date, date2: Date): boolean {
  const d1 = new Date(date1);
  d1.setHours(0, 0, 0, 0);
  const d2 = new Date(date2);
  d2.setHours(0, 0, 0, 0);
  return d1 <= d2;
}

/**
 * Format minutes to a readable time string (e.g., "8h 30m" or "45m")
 * @param totalMinutes - Total minutes to format
 * @returns Formatted time string
 */
export function formatMinutesToTime(totalMinutes: number): string {
  if (!totalMinutes || totalMinutes === 0) {
    return '-';
  }
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  
  if (hours === 0) {
    return `${minutes}m`;
  }
  
  if (minutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${minutes}m`;
}

/**
 * Format hour name from "01"-"24" to display format (e.g., "1 AM", "12 PM", "1 PM")
 * @param hour - Hour string in "01"-"24" format
 * @returns Formatted hour string (e.g., "1 AM", "12 PM", "1 PM")
 */
export function formatHourName(hour: string): string {
  const hourNum = parseInt(hour, 10);
  if (hourNum < 1 || hourNum > 24) return hour;
  
  if (hourNum === 12) return '12 PM';
  if (hourNum === 24) return '12 AM';
  if (hourNum < 12) return `${hourNum} AM`;
  return `${hourNum - 12} PM`;
}

/**
 * Get current hour in "01"-"24" format
 * API format: 1-24 where 1=1AM, 12=12PM, 13=1PM, 24=12AM
 * JavaScript: 0-23 where 0=12AM, 12=12PM, 13=1PM
 * @returns Current hour in "01"-"24" format
 */
export function getCurrentHourName(): string {
  const now = new Date();
  const jsHour = now.getHours(); // 0-23
  // Convert: JS 0 (midnight) = API 24, JS 1-23 = API 1-23
  const apiHour = jsHour === 0 ? 24 : jsHour;
  return String(apiHour).padStart(2, '0');
}

/**
 * Get all available hour names in "01"-"24" format
 * @returns Array of hour strings from "01" to "24"
 */
export function getAllHourNames(): string[] {
  return Array.from({ length: 24 }, (_, i) => String(i + 1).padStart(2, '0'));
}

/**
 * Filter locations by hour from employee's locations_history
 * @param employee - Employee location data with locations_history
 * @param hourName - Hour name in "01"-"24" format, or null for all hours
 * @returns Location history hour entry or null if not found
 */
export function filterLocationsByHour<T extends { locations_history: Array<{ name: string }> }>(
  employee: T,
  hourName: string | null
): T['locations_history'][number] | null {
  if (!hourName) return null;
  
  const hourEntry = employee.locations_history.find(
    (hour) => hour.name === hourName
  );
  
  return hourEntry || null;
}

/**
 * Type guard: check if a value is a valid location item (object with latitude/longitude).
 * API may return history as [[]] or mixed; only objects with lat/lng are valid.
 */
function isValidLocationItem(
  item: unknown
): item is { latitude: string; longitude: string; location_add_time?: string; location_add_date?: string; address?: string; id?: number } {
  return (
    typeof item === 'object' &&
    item !== null &&
    !Array.isArray(item) &&
    'latitude' in item &&
    'longitude' in item &&
    typeof (item as { latitude: unknown }).latitude === 'string' &&
    typeof (item as { longitude: unknown }).longitude === 'string'
  );
}

/**
 * Get the last present location at the branch from locations_history.
 * Among entries with attendance_status === 'present' and valid history items,
 * returns the chronologically last valid location (last present location).
 * The first lat=0, long=0 encountered is treated as punch-out / last seen:
 * it is returned as the last location and traversal stops (later entries are ignored).
 * @param locationsHistory - Array of hour entries (01-24) from get-branch-employee-locations
 * @returns The last present location item, or null if none
 */
export function getLastPresentLocation(
  locationsHistory: Array<{ attendance_status: string; history: unknown[] }>
): LocationHistoryItem | null {
  let last: LocationHistoryItem | null = null;
  outer: for (const hourEntry of locationsHistory) {
    if (hourEntry.attendance_status !== 'present') continue;
    for (const item of hourEntry.history) {
      if (!isValidLocationItem(item)) continue;
      const lat = parseFloat(item.latitude);
      const lng = parseFloat(item.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
      const locationItem: LocationHistoryItem = {
        id: item.id ?? 0,
        address: item.address ?? '',
        company_id: 0,
        company_employee_id: 0,
        latitude: item.latitude,
        longitude: item.longitude,
        location_add_time: item.location_add_time ?? '',
        location_add_date: item.location_add_date ?? '',
        status: null,
      };
      last = locationItem;
      if (lat === 0 && lng === 0) break outer;
    }
  }
  return last;
}

/**
 * Calculate distance between two lat/lng points using Haversine formula
 * @param lat1 - Latitude of first point
 * @param lng1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lng2 - Longitude of second point
 * @returns Distance in kilometers
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Distance in METRES between two coordinates on the WGS84 ellipsoid (Vincenty
 * inverse formula).
 *
 * This matches iOS `CLLocation.distance(from:)`, which measures on the same
 * ellipsoid. The spherical Haversine above over-counts by ~0.1-0.2% at mid
 * latitudes, which is what made a 6.13 km day (iOS) read 6.14 km on the web.
 * The employee-tracking pipeline measures every leg with this so its distance
 * total lands on the same number iOS shows.
 *
 * Converges in a couple of iterations for any real-world separation; falls back
 * to the spherical distance only for the (never-hit here) near-antipodal case
 * where Vincenty does not converge. Coincident points return 0.
 */
export function wgs84DistanceMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const a = 6378137; // WGS84 semi-major axis (metres)
  const f = 1 / 298.257223563; // WGS84 flattening
  const b = (1 - f) * a; // semi-minor axis
  const toRad = (deg: number): number => (deg * Math.PI) / 180;

  const L = toRad(lng2 - lng1);
  const U1 = Math.atan((1 - f) * Math.tan(toRad(lat1)));
  const U2 = Math.atan((1 - f) * Math.tan(toRad(lat2)));
  const sinU1 = Math.sin(U1);
  const cosU1 = Math.cos(U1);
  const sinU2 = Math.sin(U2);
  const cosU2 = Math.cos(U2);

  let lambda = L;
  let iterations = 100;
  let cosSqAlpha = 0;
  let sinSigma = 0;
  let cosSigma = 0;
  let sigma = 0;
  let cos2SigmaM = 0;
  let lambdaPrev: number;

  do {
    const sinLambda = Math.sin(lambda);
    const cosLambda = Math.cos(lambda);
    sinSigma = Math.sqrt(
      (cosU2 * sinLambda) ** 2 +
        (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda) ** 2
    );
    if (sinSigma === 0) return 0; // coincident points
    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
    sigma = Math.atan2(sinSigma, cosSigma);
    const sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma;
    cosSqAlpha = 1 - sinAlpha ** 2;
    // On an equatorial line cosSqAlpha is 0, leaving cos2SigmaM undefined -- it
    // is set to 0 there, per the standard formulation.
    cos2SigmaM = cosSqAlpha !== 0 ? cosSigma - (2 * sinU1 * sinU2) / cosSqAlpha : 0;
    const C = (f / 16) * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha));
    lambdaPrev = lambda;
    lambda =
      L +
      (1 - C) *
        f *
        sinAlpha *
        (sigma +
          C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2)));
  } while (Math.abs(lambda - lambdaPrev) > 1e-12 && --iterations > 0);

  // Non-convergence (near-antipodal): fall back to the spherical distance.
  if (iterations === 0) return calculateDistance(lat1, lng1, lat2, lng2) * 1000;

  const uSq = (cosSqAlpha * (a * a - b * b)) / (b * b);
  const A = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
  const deltaSigma =
    B *
    sinSigma *
    (cos2SigmaM +
      (B / 4) *
        (cosSigma * (-1 + 2 * cos2SigmaM ** 2) -
          (B / 6) *
            cos2SigmaM *
            (-3 + 4 * sinSigma ** 2) *
            (-3 + 4 * cos2SigmaM ** 2)));

  return b * A * (sigma - deltaSigma);
}

/**
 * Calculate time duration between two timestamps in milliseconds
 * @param startTime - Start timestamp in milliseconds
 * @param endTime - End timestamp in milliseconds
 * @returns Duration in minutes
 */
export function calculateDurationMinutes(startTime: number, endTime: number): number {
  if (!startTime || !endTime) return 0;
  const diffMs = endTime - startTime;
  return Math.round(diffMs / (1000 * 60)); // Convert to minutes
}

/**
 * Format duration to display format (e.g., "6hrs 4mins", "2hrs 0mins", "45mins")
 * @param totalMinutes - Total minutes to format
 * @returns Formatted duration string
 */
export function formatDuration(totalMinutes: number): string {
  if (!totalMinutes || totalMinutes === 0) {
    return '0mins';
  }
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  
  if (hours === 0) {
    return `${minutes}mins`;
  }
  
  if (minutes === 0) {
    return `${hours}hrs`;
  }
  
  return `${hours}hrs ${minutes}mins`;
}

/**
 * Parse API decimal hours (string or number) and format like formatDuration (e.g. "8hrs 29mins").
 * Non-finite or ≤0 values format as "0mins".
 */
export function formatDecimalHoursAsDuration(decimalHours: string | number | undefined | null): string {
  const n = typeof decimalHours === 'string' ? parseFloat(decimalHours) : Number(decimalHours);
  if (!Number.isFinite(n) || n <= 0) {
    return formatDuration(0);
  }
  return formatDuration(Math.round(n * 60));
}

/** True if decimal hour string/number parses to a finite value greater than zero. */
export function isDecimalHoursPositive(decimalHours: string | number | undefined | null): boolean {
  const n = typeof decimalHours === 'string' ? parseFloat(decimalHours) : Number(decimalHours);
  return Number.isFinite(n) && n > 0;
}

/**
 * Format distance to display format (e.g., "0.02 km", "1.5 km")
 * @param distanceKm - Distance in kilometers
 * @returns Formatted distance string
 */
export function formatDistance(distanceKm: number): string {
  if (!distanceKm || distanceKm === 0) {
    return '0 km';
  }
  
  // Round to 2 decimal places
  const rounded = Math.round(distanceKm * 100) / 100;
  return `${rounded} km`;
}

/**
 * Combine a date with a specific time, ensuring proper local time handling
 * @param date - Date object (can be just a date, time will be preserved or set)
 * @param hours - Hour (0-23)
 * @param minutes - Minutes (0-59)
 * @param seconds - Seconds (0-59), defaults to 0
 * @returns New Date object with the specified date and time in local timezone
 */
export function setDateTime(
  date: Date,
  hours: number,
  minutes: number,
  seconds: number = 0
): Date {
  const newDate = new Date(date);
  newDate.setHours(hours, minutes, seconds, 0);
  return newDate;
}

/**
 * Format date for datetime-local input (YYYY-MM-DDTHH:mm)
 * Handles timezone correctly for datetime-local inputs
 * @param date - Date object to format
 * @returns Formatted string for datetime-local input
 */
export function formatDateTimeLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Parse datetime-local input value to Date object
 * @param value - String from datetime-local input (YYYY-MM-DDTHH:mm)
 * @returns Date object in local timezone
 */
export function parseDateTimeLocal(value: string): Date {
  // datetime-local inputs are in local timezone, so we can parse directly
  return new Date(value);
}

/**
 * Convert a local Date object to a timestamp that represents the same moment in time
 * This is used when sending dates to APIs that expect timestamps
 * The timestamp represents the same moment in time regardless of timezone
 * @param date - Date object in local timezone
 * @returns Timestamp in milliseconds (UTC epoch time)
 */
export function dateToTimestamp(date: Date): number {
  // getTime() already returns UTC timestamp, which is what we want
  // This represents the same moment in time regardless of timezone
  return date.getTime();
}
